import { useMeeting, useParticipant } from '@videosdk.live/react-sdk'
import { useEffect, useMemo, useRef } from 'react'
import ReactPlayer from 'react-player'
import MicOffSmallIcon from '../icons/MicOffSmallIcon'
import ScreenShareIcon from '../icons/ScreenShareIcon'
import SpeakerIcon from '../icons/SpeakerIcon'
import { nameTruncated } from '../utils/helper'
import { useMeetingAppContext } from '../MeetingAppContextDef'
import { CornerDisplayName } from './CornerDisplayName' // ⬅️ matches your current filename

type PresenterViewProps = {
  /** Pixel height computed by parent */
  height: number
}

export function PresenterView({ height }: PresenterViewProps) {
  const mMeeting = useMeeting()
  const { allowAutoAudio } = useMeetingAppContext()
  const presenterId = mMeeting?.presenterId

  // Hooks must not be conditional; pass empty string if not available
  const {
    micOn,
    webcamOn,
    isLocal,
    screenShareStream,
    screenShareAudioStream,
    screenShareOn,
    displayName,
    isActiveSpeaker,
  } = useParticipant(presenterId || '') as any

  const videoPlayer = useRef<ReactPlayer | null>(null)
  const audioPlayer = useRef<HTMLAudioElement | null>(null)

  const mediaStream: MediaStream | undefined = useMemo(() => {
    if (screenShareOn && screenShareStream?.track) {
      const ms = new MediaStream()
      ms.addTrack(screenShareStream.track)
      return ms
    }
    return undefined
  }, [screenShareStream, screenShareOn])

  useEffect(() => {
    if (!allowAutoAudio) return
    if (!isLocal && audioPlayer.current && screenShareOn && screenShareAudioStream?.track) {
      const ms = new MediaStream()
      ms.addTrack(screenShareAudioStream.track)
      ;(audioPlayer.current as any).srcObject = ms
      audioPlayer.current.play().catch(() => {
        // ignore autoplay-block errors
      })
    } else if (audioPlayer.current) {
      ;(audioPlayer.current as any).srcObject = null
    }
  }, [screenShareAudioStream, screenShareOn, isLocal, allowAutoAudio])

  // No presenter => render nothing
  if (!presenterId) return null

  return (
    <div className="bg-gray-750 rounded m-2 relative overflow-hidden w-full" style={{ height }}>
      <audio autoPlay playsInline controls={false} ref={audioPlayer} />
      <div className="video-contain absolute h-full w-full">
        <ReactPlayer
          ref={videoPlayer}
          playsinline
          playIcon={<></>}
          pip={false}
          light={false}
          controls={false}
          muted={true}
          playing={true}
          url={mediaStream as any}
          height="100%"
          width="100%"
          style={{ filter: isLocal ? 'blur(1rem)' : undefined }}
          onError={(err) => {
            // no-op
          }}
        />

        <div
          className="bottom-2 left-2 bg-gray-750 p-2 absolute rounded-md flex items-center justify-center"
          style={{ transition: 'all 200ms', transitionTimingFunction: 'linear' }}
        >
          {!micOn ? (
            <MicOffSmallIcon fillcolor="white" />
          ) : micOn && isActiveSpeaker ? (
            <SpeakerIcon />
          ) : null}

          <p className="text-sm text-white ml-2">
            {isLocal ? `You are presenting` : `${nameTruncated(displayName, 15)} is presenting`}
          </p>
        </div>

        {isLocal && (
          <>
            <div className="p-10 rounded-2xl flex flex-col items-center justify-center absolute top-1/2 left-1/2 bg-gray-750 transform -translate-x-1/2 -translate-y-1/2">
              <ScreenShareIcon style={{ height: 48, width: 48, color: 'white' }} />
              <div className="mt-4">
                <p className="text-white text-xl font-semibold">You are presenting to everyone</p>
              </div>
              <div className="mt-8">
                <button
                  className="bg-purple-550 text-white px-4 py-2 rounded text-sm text-center font-medium"
                  onClick={(e) => {
                    e.stopPropagation()
                    mMeeting.toggleScreenShare()
                  }}
                >
                  STOP PRESENTING
                </button>
              </div>
            </div>
            <CornerDisplayName
              {...{
                isLocal,
                displayName,
                micOn,
                webcamOn,
                isPresenting: true,
                participantId: presenterId,
                isActiveSpeaker,
              }}
            />
          </>
        )}
      </div>
    </div>
  )
}
